import { useEffect } from 'react'
import { create } from 'zustand'

/**
 * Lets a page tell the GLOBAL breadcrumb band what its last crumb should say.
 *
 * The band (`Breadcrumbs.jsx`) is mounted by `Layout` as a sibling of `<main>`,
 * so it renders *above* the routed page and cannot see anything the page knows.
 * It builds its crumbs from path segments, which is fine for `/reports/queue`
 * ("Queue") and useless for `/reports/190` — that would read **"Home / Reports /
 * 190"**, the raw wire id. That is exactly why the band used to stand down on
 * report routes and let `ReportActionBar` draw an in-page trail instead, and why
 * the app ended up with two breadcrumb implementations that looked different.
 *
 * One trail now, in one place, and the page supplies the one thing the route
 * cannot carry: the report's name.
 *
 * A store rather than a context because the producer and the consumer are on
 * opposite sides of `Layout`'s tree — the band is a sibling of the `<Outlet />`,
 * not a descendant — so a provider would have to wrap both and every page would
 * still need the hook. zustand is already a dependency (`preferencesStore`).
 *
 * An earlier version of this idea read the report name out of react-query's
 * `['report-def', id]` cache from inside the band. Do not go back to that: it
 * only ever worked for the 119 generic reports (the 21 specialized pages take
 * their name as a prop and never populate that key), and it made the chrome
 * quietly dependent on another component's cache key.
 */
export const useBreadcrumbLabelStore = create((set) => ({
  /** `{ [pathname]: label }` — keyed by path so a stale label can never be shown
   *  on a route it did not come from. */
  labels: {},
  setLabel: (pathname, label) =>
    set((s) => (s.labels[pathname] === label ? s : { labels: { ...s.labels, [pathname]: label } })),
  clearLabel: (pathname) =>
    set((s) => {
      if (!(pathname in s.labels)) return s
      const next = { ...s.labels }
      delete next[pathname]
      return { labels: next }
    }),
}))

/**
 * Publish this page's breadcrumb label. Call it with the report's name; call it
 * with a falsy value while the name is still loading and the band falls back to
 * its generic crumb rather than showing the raw id.
 *
 * Keyed by pathname and cleared on unmount, so navigating from one report to
 * another cannot leave the previous report's name in the trail — the failure
 * mode a single `currentLabel` slot would have.
 */
export const usePublishBreadcrumbLabel = (pathname, label) => {
  const setLabel = useBreadcrumbLabelStore((s) => s.setLabel)
  const clearLabel = useBreadcrumbLabelStore((s) => s.clearLabel)

  useEffect(() => {
    if (label) setLabel(pathname, label)
    else clearLabel(pathname)
    return () => clearLabel(pathname)
  }, [pathname, label, setLabel, clearLabel])
}

/** Read the label published for a pathname, if any. */
export const useBreadcrumbLabel = (pathname) =>
  useBreadcrumbLabelStore((s) => s.labels[pathname])
